import { useEffect, useMemo, useState } from "react";
import { COMMISSION_BASIS_LABEL, commissionEarnedCents, type Invoice } from "@clevis/shared-types";
import { Card, CardTitle, cn } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";
import { downloadCsv, csvMoney } from "../lib/csv";
import { useSalesSettings } from "../hooks/useSalesSettings";

// Commissions for the period: each estimate owner's share of what they sold,
// figured the company's way (Team → Commissions) at the rate each person
// actually gets (their own, else their employee type's default). Rates are
// pay data, so this card is admin-only.

const money = (c: number) => (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

interface Row {
  uid: string;
  name: string;
  rateBps: number | null;
  baseCents: number;
  commissionCents: number;
  invoices: Array<{ number: number; customer: string; baseCents: number; commissionCents: number }>;
}

export function CommissionsCard({ tenantId, invoices, start, end, rangeLabel }: { tenantId: string; invoices: Array<Invoice & { id: string }>; start: number; end: number; rangeLabel: string }) {
  const settings = useSalesSettings(tenantId);
  const [rates, setRates] = useState<Record<string, number | null> | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    void call<{ settings: Record<string, { commissionRateBps?: number | null; effectiveCommissionRateBps?: number | null }> }>("getTeamSettings", { tenantId }, { mutation: false })
      .then((r) => setRates(Object.fromEntries(Object.entries(r.settings).map(([uid, s]) => [uid, s.effectiveCommissionRateBps ?? s.commissionRateBps ?? null]))))
      .catch(() => setRates({}));
  }, [tenantId]);

  const rows = useMemo(() => {
    const by = new Map<string, Row>();
    for (const inv of invoices) {
      if (!inv.salespersonUid) continue;
      const earned = commissionEarnedCents(inv, settings.commissionBasis, settings.commissionWhen, start, end);
      if (earned <= 0) continue;
      const rate = rates?.[inv.salespersonUid] ?? null;
      const commission = rate ? Math.round((earned * rate) / 10000) : 0;
      const row = by.get(inv.salespersonUid) ?? { uid: inv.salespersonUid, name: inv.salespersonName || "Teammate", rateBps: rate, baseCents: 0, commissionCents: 0, invoices: [] };
      row.baseCents += earned;
      row.commissionCents += commission;
      row.invoices.push({ number: inv.number, customer: inv.customerName, baseCents: earned, commissionCents: commission });
      by.set(inv.salespersonUid, row);
    }
    return [...by.values()].sort((a, b) => b.commissionCents - a.commissionCents || b.baseCents - a.baseCents);
  }, [invoices, settings.commissionBasis, settings.commissionWhen, start, end, rates]);

  const unowned = useMemo(
    () => invoices.filter((i) => !i.salespersonUid && commissionEarnedCents(i, settings.commissionBasis, settings.commissionWhen, start, end) > 0).length,
    [invoices, settings.commissionBasis, settings.commissionWhen, start, end],
  );
  const total = rows.reduce((s, r) => s + r.commissionCents, 0);
  const how = `on ${COMMISSION_BASIS_LABEL[settings.commissionBasis]}, ${settings.commissionWhen === "collected" ? "as customers pay" : "when invoices are sent"}`;

  function exportCsv() {
    downloadCsv(
      "commissions",
      ["Salesperson", "Invoice", "Customer", "Commission base", "Rate %", "Commission"],
      rows.flatMap((r) => r.invoices.map((i) => [r.name, i.number, i.customer, csvMoney(i.baseCents), r.rateBps !== null ? String(r.rateBps / 100) : "", csvMoney(i.commissionCents)])),
    );
  }

  return (
    <Card>
      <div className="mb-1 flex items-center justify-between gap-2">
        <CardTitle className="mb-0">Commissions · {rangeLabel}</CardTitle>
        {rows.length ? (
          <button className="text-xs text-steel hover:text-ink" onClick={exportCsv}>
            Export CSV
          </button>
        ) : null}
      </div>
      <p className="mb-2 text-xs text-steel">Figured {how}. Change it under Team → Commissions.</p>
      {rates === null ? <p className="text-xs text-steel">Loading rates…</p> : null}
      {rates !== null && rows.length === 0 ? (
        <p className="text-xs text-steel">No commissions in this period. An estimate's owner earns them once its invoice is {settings.commissionWhen === "collected" ? "paid" : "sent"}.</p>
      ) : null}
      {rows.length ? (
        <div className="divide-y divide-steel/10 text-sm">
          {rows.map((r) => (
            <div key={r.uid}>
              <button className="flex w-full items-center gap-3 py-1.5 text-left hover:bg-cream/50" onClick={() => setOpen(open === r.uid ? null : r.uid)}>
                <span className="min-w-0 flex-1 truncate text-ink">{r.name}</span>
                <span className="w-24 text-right text-xs text-steel">{r.invoices.length} invoice{r.invoices.length === 1 ? "" : "s"}</span>
                <span className="w-28 text-right text-xs text-steel">{money(r.baseCents)}</span>
                <span className={cn("w-16 text-right text-xs", r.rateBps ? "text-steel" : "text-brandorange-dark")}>{r.rateBps ? `${r.rateBps / 100}%` : "no rate"}</span>
                <span className="w-24 text-right font-semibold text-ink">{money(r.commissionCents)}</span>
              </button>
              {open === r.uid ? (
                <div className="mb-1 rounded bg-cream/50 px-2 py-1 text-xs">
                  {r.invoices.map((i) => (
                    <div key={i.number} className="flex gap-3 py-0.5">
                      <span className="w-14 text-steel">#{i.number}</span>
                      <span className="min-w-0 flex-1 truncate text-ink">{i.customer || "No customer"}</span>
                      <span className="w-24 text-right text-steel">{money(i.baseCents)}</span>
                      <span className="w-20 text-right text-ink">{money(i.commissionCents)}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ))}
          <div className="flex items-center justify-between pt-1.5 text-sm">
            <span className="text-steel">Total</span>
            <span className="font-bold text-ink">{money(total)}</span>
          </div>
        </div>
      ) : null}
      {rows.some((r) => !r.rateBps) ? <p className="mt-1 text-xs text-brandorange-dark">Someone here has no commission rate yet: Team → Commissions sets a default per employee type, or open the person under People.</p> : null}
      {unowned ? <p className="mt-1 text-xs text-steel">{unowned} invoice{unowned === 1 ? " has" : "s have"} no owner and earn no commission (set who sold it on the invoice).</p> : null}
    </Card>
  );
}

